import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, KeyRound } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function SetPassword() {
  const { token } = useParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get(`/auth/invite/${token}`)
      .then((r) => setInvite(r.data))
      .catch((err) => setError(err?.response?.data?.detail || "Invitation not valid"))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    if (pw !== pw2) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/set-password", { token, new_password: pw });
      localStorage.setItem("ll_token", data.token);
      toast.success("Password set. Welcome to LingoFacil!");
      await refresh();
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not set password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grain-bg flex items-center justify-center px-4 py-12" data-testid="set-password-page">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 mb-8">
          <div className="w-12 h-12 rounded-2xl bg-brand-yellow border-2 border-brand-ink shadow-brutal flex items-center justify-center">
            <span className="font-display font-bold text-2xl text-brand-navy">L</span>
          </div>
          <span className="font-display font-bold text-3xl text-brand-navy">LingoFacil</span>
        </Link>

        <div className="brutal-card p-8">
          <div className="brutal-badge bg-brand-mint text-xs mb-3">
            <KeyRound className="w-3.5 h-3.5" strokeWidth={3} /> First-time setup
          </div>

          {loading && <p className="text-brand-navy/70">Checking your invitation…</p>}

          {!loading && error && (
            <>
              <h1 className="font-display text-3xl font-bold text-brand-navy mb-2">Invitation problem</h1>
              <p className="text-brand-navy/70 mb-4" data-testid="set-password-error">{error}</p>
              <Link to="/login" className="brutal-btn-pink">Go to login</Link>
            </>
          )}

          {!loading && invite && (
            <>
              <h1 className="font-display text-3xl font-bold text-brand-navy mb-1">
                Welcome, {invite.name || invite.email.split("@")[0]}!
              </h1>
              <p className="text-brand-navy/70 mb-6">
                Pick a password to activate <span className="font-mono">{invite.email}</span>.
              </p>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="text-sm font-bold uppercase text-brand-navy tracking-wide">New password</label>
                  <input
                    type="password"
                    required
                    minLength={6}
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    data-testid="set-password-new"
                    className="brutal-input mt-1"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="text-sm font-bold uppercase text-brand-navy tracking-wide">Confirm password</label>
                  <input
                    type="password"
                    required
                    minLength={6}
                    value={pw2}
                    onChange={(e) => setPw2(e.target.value)}
                    data-testid="set-password-confirm"
                    className="brutal-input mt-1"
                  />
                </div>
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="set-password-submit"
                  className="brutal-btn-pink w-full text-lg py-4"
                >
                  {busy ? "Setting…" : (<>Set password & sign in <ArrowRight className="w-5 h-5" strokeWidth={3} /></>)}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
